import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CookieOverlay from 'components/CookieOverlay';
import LoadingDots from 'components/LoadingDots';
import ScrollToTop from 'components/ScrollToTop';
import type { Location } from 'history';
import { TrackRouteChanges } from 'hooks/useTrackRouteChanges';
import AbTestsRoute from 'modules/ab-tests';
import AppUrlListener from 'modules/app/AppUrlListener';
import ModalOverlays from 'modules/app/ModalOverlays';
import PasswordRoutes from 'modules/authentication/PasswordRoutes';
import { AuthCheck } from 'modules/authentication/helpers/nextauth';
import CheckoutRoutes from 'modules/checkout';
import ConfirmationRoute from 'modules/confirmation';
import CourseRoutes from 'modules/course';
import Notfound from 'modules/errorpages/Notfound';
import ServerError from 'modules/errorpages/ServerError';
import ExperienceRoutes from 'modules/experience';
import ExperiencesRoutes from 'modules/experiences';
import FavoritesRoutes from 'modules/favorites';
import FeatureFlagRoute from 'modules/feature-flags';
import DefaultHead from 'modules/head';
import HomescreenRoutes from 'modules/homescreen';
import HostRoute from 'modules/host';
import HostAgendaRoutes from 'modules/host-agenda';
import HostExperiencesRoutes from 'modules/host-experiences';
import {
  HostHomePageRoutes,
  HostHomePageWidgetRoutes,
} from 'modules/host-homescreen';
import HostLearningCenter from 'modules/host-learning-center';
import HostOnboardingPageRoute from 'modules/host-onboarding';
import HostPayoutRoutes from 'modules/host-payout-methods';
import PersonalizedOfferRoutes from 'modules/host-personalized-offer';
import HostProfile from 'modules/host-profile';
import HostStatusRoutes from 'modules/host-status';
import HostTransferBooking from 'modules/host-transfer-booking';
import InfoPagesRoutes from 'modules/info-pages';
import InvitationRoutes from 'modules/invite';
import AppPromoRoute from 'modules/landings/app-promo';
import SustainabilityRoute from 'modules/landings/sustainability';
import LocalSearchRoutes from 'modules/locals';
import ManageBookingRoutes from 'modules/manage-booking';
import OverviewPageRoutes from 'modules/overview';
import PrismicRoutes from 'modules/prismic';
import PrivacyRoute from 'modules/privacy';
import RateAppRoute from 'modules/rate-app';
import RescheduleRoutes from 'modules/reschedule';
import ReviewPageRoute from 'modules/review-page';
import SearchRoutes from 'modules/search';
import UserEnvironment2Routes from 'modules/user-environment2';
import WriteReviewRoutes from 'modules/write-review';
import { CookieConsentContextProvider } from 'providers/CookieConsentProvider';
import { FavoritesContextProvider } from 'providers/FavoritesProvider';
import React, { useEffect, useRef, useState } from 'react';
import { Switch, useHistory, useLocation } from 'react-router-dom';
import { scrollToFocusedOnKeyboard } from 'utils/onResize';
import { isCordova, isIosApp } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import { CurrencyContextProvider } from '~/context/CurrencyContext';
import { CurrenciesListContextProvider } from '~/context/CurrencyListContext';
import { ExperimentsContextProvider } from '~/context/ExperimentsContext';
import { UserEnvironmentContextProvider } from '~/context/UserEnvironmentContext';
import { appServices } from '~/singletons';
import { useInAppUpdateStore } from '~/stores/inAppUpdate';

import BackUrlHandler from './BackUrlHandler';

export const queryClient = new QueryClient();

export type ModalLocationState = { modal?: boolean };

async function CapacitorSplashScreenInitialization() {
  await SplashScreen.hide();
}

export async function CapacitorStatusBarInitialization() {
  await StatusBar.setOverlaysWebView({ overlay: false });
  await StatusBar.setBackgroundColor({ color: '#FFFBF8' });
  await StatusBar.setStyle({ style: Style.Default });
  if (isIosApp()) {
    await StatusBar.hide();
  }
  await StatusBar.show();
}

async function CapacitorPluginInitialization({ history }: { history: any }) {
  await CapacitorSplashScreenInitialization();
  await CapacitorStatusBarInitialization();

  appServices.permissionFunctions.startPush();

  await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (!canGoBack) {
      // If there is no history to go back to, we exit the app
      CapacitorApp.exitApp();
      return;
    }
    // If there is history, we let the router handle the back navigation
    // This is important to ensure that the app behaves correctly on Android
    history.goBack();
  });

  await CapacitorApp.addListener('resume', () => {
    useInAppUpdateStore
      .getState()
      .checkForUpdate({ startWhenAvailable: false, trigger: 'auto' });
  });
}

const App = () => {
  const history = useHistory();
  const location = useLocation<ModalLocationState>();
  const previousLocation = useRef<Location<ModalLocationState>>(
    location as Location<ModalLocationState>,
  );
  const [underlyingLocation, setUnderlyingLocation] =
    useState<Location<ModalLocationState> | null>(null);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const { checkForUpdate } = useInAppUpdateStore();

  useEffect(() => {
    if (isCordova() && hasHydrated) {
      void checkForUpdate({ startWhenAvailable: true, trigger: 'auto' });
      setTimeout(async () => {
        await CapacitorPluginInitialization({ history });
      });
    }
    const unsubscribe = scrollToFocusedOnKeyboard();
    return () => {
      unsubscribe();
    };
  }, [history, checkForUpdate, hasHydrated]);
  const isModal =
    !!location?.state?.modal && !previousLocation.current?.state?.modal;
  useEffect(() => {
    if (isModal) {
      // If we are opening a modal, we want to keep the previous location
      // so we can render it in the background.
      setUnderlyingLocation(previousLocation.current);
    } else {
      setUnderlyingLocation(location);
    }
    previousLocation.current = location;
  }, [isModal, location]);

  if (!hasHydrated) {
    return <LoadingDots />;
  }

  return (
    <CookieConsentContextProvider>
      <QueryClientProvider client={queryClient} key="query-client">
        <ExperimentsContextProvider>
          <UserEnvironmentContextProvider>
            <CurrenciesListContextProvider>
              <CurrencyContextProvider>
                <FavoritesContextProvider>
                  <ScrollToTop
                    isModal={isModal}
                    history={history}
                    newLocation={underlyingLocation}
                  />
                  <AppUrlListener />
                  <DefaultHead />
                  <BackUrlHandler />
                  <AuthCheck />
                  <TrackRouteChanges />
                  <Switch location={underlyingLocation}>
                    {ModalOverlays}
                    {PrismicRoutes}
                    {HomescreenRoutes}
                    {HostHomePageWidgetRoutes}
                    {HostHomePageRoutes}
                    {HostOnboardingPageRoute}
                    {HostLearningCenter}
                    {HostProfile}
                    {HostTransferBooking}
                    {SearchRoutes}
                    {ExperiencesRoutes}
                    {LocalSearchRoutes}
                    {ExperienceRoutes}
                    {CheckoutRoutes}
                    {ConfirmationRoute}
                    {HostAgendaRoutes}
                    {RateAppRoute}
                    {ReviewPageRoute}
                    {FavoritesRoutes}
                    {CourseRoutes}
                    {PrivacyRoute}
                    {AppPromoRoute}
                    {PersonalizedOfferRoutes}
                    {HostPayoutRoutes}
                    {HostExperiencesRoutes}
                    {HostStatusRoutes}
                    {UserEnvironment2Routes}
                    {HostRoute}
                    {FeatureFlagRoute}
                    {AbTestsRoute}
                    {SustainabilityRoute}
                    {WriteReviewRoutes}
                    {InfoPagesRoutes}
                    {PasswordRoutes}
                    {ManageBookingRoutes}
                    {OverviewPageRoutes}
                    {InvitationRoutes}
                    {RescheduleRoutes}
                    {ServerError}
                    <Notfound />
                  </Switch>
                  <CookieOverlay />
                  {isModal && (
                    <Switch>
                      {ModalOverlays}
                      {PrismicRoutes}
                      {HostHomePageWidgetRoutes}
                      {HostHomePageRoutes}
                      {HostOnboardingPageRoute}
                      {HomescreenRoutes}
                      {HostLearningCenter}
                      {HostExperiencesRoutes}
                      {HostStatusRoutes}
                      {HostProfile}
                      {HostTransferBooking}
                      {SearchRoutes}
                      {ExperiencesRoutes}
                      {LocalSearchRoutes}
                      {ExperienceRoutes}
                      {CheckoutRoutes}
                      {ConfirmationRoute}
                      {HostAgendaRoutes}
                      {RateAppRoute}
                      {ReviewPageRoute}
                      {FavoritesRoutes}
                      {CourseRoutes}
                      {PrivacyRoute}
                      {AppPromoRoute}
                      {PersonalizedOfferRoutes}
                      {UserEnvironment2Routes}
                      {ManageBookingRoutes}
                      {UserEnvironment2Routes}
                      {HostRoute}
                      {ModalOverlays}
                      {FeatureFlagRoute}
                      {AbTestsRoute}
                      {WriteReviewRoutes}
                      {InfoPagesRoutes}
                      {PasswordRoutes}
                      {OverviewPageRoutes}
                      {RescheduleRoutes}
                    </Switch>
                  )}
                </FavoritesContextProvider>
              </CurrencyContextProvider>
            </CurrenciesListContextProvider>
          </UserEnvironmentContextProvider>
        </ExperimentsContextProvider>
      </QueryClientProvider>
    </CookieConsentContextProvider>
  );
};

export default App;
